import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import dryftService from '../services/dryftService';
import hotelService from '../services/hotelService';
import OhipHotelCredentialStep from './OhipHotelCredentialStep';
import DryftRoutesPanel from './DryftRoutesPanel';
import DryftDryRunPanel from './DryftDryRunPanel';
import DryftChangesPanel from './DryftChangesPanel';
import './ProfileIATAPage.css';
import './OperaCloudCompare.css';
import { Radar } from 'lucide-react';
import Tabs from './shared/Tabs';
import ProductSupportTab, { SUPPORT_TAB, useSupportTab } from './support/ProductSupportTab';

// Dryft project detail — project info + hotel management (each hotel has its own OHIP credential),
// the monitoring routes (config set → notification target) + run frequency, a live dry-run, and the
// recent config changes the scheduled snapshot job detected.

const DryftDetailPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [tab, setTab] = useSupportTab('project');   // Project | Support
    const { isSuperAdmin, isPartner } = useAuth();
    const { selectedAccount, selectedPartner, isChainAdmin } = useAccount();

    const [project, setProject] = useState(null);
    const [hotels, setHotels] = useState([]);
    const [allHotels, setAllHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [adding, setAdding] = useState(false);
    const [saving, setSaving] = useState(false);
    const [addError, setAddError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await dryftService.get(id);
            setProject(res.data?.project || null);
            setHotels(res.data?.hotels || []);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load project');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    // Hotels available to add — scoped by the chosen chain/partner, same as the wizard.
    const loadAllHotels = useCallback(async () => {
        try {
            const res = await hotelService.getAllHotels(
                selectedAccount ? { accountid: selectedAccount } : { partnerid: selectedPartner }
            );
            setAllHotels(res.hotels || []);
        } catch (err) {
            console.error('Failed to load hotels', err);
        }
    }, [selectedAccount, selectedPartner]);

    useEffect(() => { loadAllHotels(); }, [loadAllHotels]);

    const handleAdd = async ({ hotel_id, credential }) => {
        if (saving) return;
        setSaving(true);
        setAddError(null);
        try {
            await dryftService.addHotel(id, { hotel_id, credential });
            setAdding(false);
            await load();
        } catch (err) {
            setAddError(err.response?.data?.message || err.message || 'Failed to add hotel');
        } finally {
            setSaving(false);
        }
    };

    const handleRemove = async (hotelRowId) => {
        if (!window.confirm('Remove this hotel from the project?')) return;
        try {
            await dryftService.removeHotel(id, hotelRowId);
            setHotels(prev => prev.filter(h => h.id !== hotelRowId));
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to remove hotel');
        }
    };

    if (loading) {
        return <div className="pi-page"><div className="pi-empty"><p className="pi-empty-text">Loading…</p></div></div>;
    }
    if (error && !project) {
        return (
            <div className="pi-page">
                <div className="ppw-error-msg" style={{ margin: '8px 0' }}>{error}</div>
                <button className="pi-back-btn" onClick={() => navigate('/apps/dryft')}>← Back to Dryft</button>
            </div>
        );
    }

    if (adding) {
        const excludeHotelIds = hotels.map(h => h.hotel_id);
        return (
            <div className="ppw-page">
                <OhipHotelCredentialStep
                    subtitle="Choose the hotel to monitor and the OHIP credential used to read its property configuration."
                    hotels={allHotels}
                    excludeHotelIds={excludeHotelIds}
                    selectedAccount={selectedAccount}
                    selectedPartner={selectedPartner}
                    isSuperAdmin={isSuperAdmin}
                    isPartner={isPartner}
                    isChainAdmin={isChainAdmin}
                    submitLabel="Add hotel"
                    saving={saving}
                    error={addError}
                    onCancel={() => { setAdding(false); setAddError(null); }}
                    onAdd={handleAdd}
                />
            </div>
        );
    }

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate('/apps/dryft')}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Dryft
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">{project?.name}</h1>
                        <p className="pi-subtitle">
                            {project?.description || 'OHIP property-config monitoring across hotels'}
                            {project?.active ? '' : ' · Inactive'}
                        </p>
                    </div>
                </div>
                <div className="pi-header-actions">
                    <button className="pi-btn-add" onClick={() => { setAddError(null); setAdding(true); }}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Add Hotel
                    </button>
                </div>
            </div>

            {error && <div className="ppw-error-msg" style={{ margin: '8px 0' }}>{error}</div>}

            <Tabs tabs={[{ key: 'project', label: 'Project', icon: <Radar size={20} /> }, SUPPORT_TAB]} active={tab} onChange={setTab} />

            {tab === 'support' ? (
                <ProductSupportTab product="Dryft" productId={id} noun="this Dryft project"
                    context={() => ({ screen: 'dryft-project', dryftProjectId: id, projectName: project?.name })} />
            ) : (<>
            {/* Hotels */}
            <div className="pi-list-panel">
                <div style={{ padding: '12px 16px 4px', fontWeight: 600, fontSize: 14, color: '#374151' }}>Hotels ({hotels.length})</div>
                {hotels.length === 0 ? (
                    <div className="pi-empty">
                        <p className="pi-empty-title">No hotels yet</p>
                        <p className="pi-empty-text">Add a hotel and its OHIP credential to start monitoring its property configuration.</p>
                        <button className="pi-btn-add" onClick={() => { setAddError(null); setAdding(true); }}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Add Hotel
                        </button>
                    </div>
                ) : (
                    <div className="pi-list">
                        {hotels.map(h => (
                            <div key={h.id} className="pi-list-item">
                                <div className="pi-item-main">
                                    <div className="pi-item-name">{h.hotel_name || `Hotel ${h.hotel_id}`}</div>
                                    <div className="pi-item-sub">
                                        {h.credential_name ? `OHIP: ${h.credential_name}` : 'No credential'}
                                        {h.hotelid_override ? ` · Hotel ID: ${h.hotelid_override}` : ''}
                                    </div>
                                </div>
                                {isSuperAdmin && (
                                    <button className="occ-link-btn" onClick={() => handleRemove(h.id)}>Remove</button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Monitoring — run frequency + routes (config set → notification target). */}
            <DryftRoutesPanel dryftId={id} project={project} />

            {/* Live dry-run — fetch a hotel's watched config from OHIP now and preview counts. */}
            <DryftDryRunPanel dryftId={id} project={project} hotels={hotels} />

            {/* Changes the scheduled OHIP snapshot job detected in the watched sub-areas. */}
            <DryftChangesPanel dryftId={id} />
            </>)}
        </div>
    );
};

export default DryftDetailPage;
